/**
 * Flat vector illustrations for the About page team cards.
 * Stylized figures in AST blue, not likenesses.
 */

const SKIN = '#C9DDF2';
const NECK = '#9DBFE3';
const EARS = '#B9D3EE';
const INK = '#0B2A4A';

function Face() {
  return (
    <>
      <circle cx="100" cy="98" r="7" fill={EARS} />
      <circle cx="164" cy="98" r="7" fill={EARS} />
      <circle cx="132" cy="94" r="33" fill={SKIN} />
    </>
  );
}

function Features() {
  return (
    <>
      <circle cx="120" cy="98" r="3" fill={INK} />
      <circle cx="144" cy="98" r="3" fill={INK} />
      <path d="M122 110 Q132 118 142 110" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
    </>
  );
}

function Dots({ x, y }: { x: number; y: number }) {
  return (
    <g fill="#B2D1F0" opacity="0.7">
      {[0, 16, 32].flatMap((dy) => [0, 16, 32].map((dx) => <circle key={`${dx}-${dy}`} cx={x + dx} cy={y + dy} r="2" />))}
    </g>
  );
}

/** Manager in front of a planning board. */
export function ManagerArt({ label }: { label: string }) {
  return (
    <svg viewBox="0 0 320 220" width="100%" role="img" aria-label={label} style={{ display: 'block' }}>
      <Dots x={24} y={24} />
      <circle cx="132" cy="120" r="88" fill="#D6E6F7" />
      <rect x="196" y="30" width="104" height="92" rx="8" fill="#FFFFFF" stroke="#B2D1F0" strokeWidth="2" />
      <rect x="206" y="42" width="24" height="6" rx="3" fill="#005EAD" />
      <rect x="236" y="42" width="24" height="6" rx="3" fill="#7FB0E0" />
      <rect x="266" y="42" width="24" height="6" rx="3" fill="#F79009" />
      <rect x="206" y="56" width="24" height="18" rx="3" fill="#EAF2FB" stroke="#B2D1F0" />
      <rect x="206" y="80" width="24" height="18" rx="3" fill="#EAF2FB" stroke="#B2D1F0" />
      <rect x="236" y="56" width="24" height="18" rx="3" fill="#EAF2FB" stroke="#B2D1F0" />
      <rect x="266" y="56" width="24" height="18" rx="3" fill="#FFF4E5" stroke="#FEC84B" />
      <rect x="266" y="80" width="24" height="18" rx="3" fill="#FFF4E5" stroke="#FEC84B" />
      <path d="M211 104 L218 111 L228 100" fill="none" stroke="#17B26A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M62 220 C62 168 90 146 132 146 C174 146 202 168 202 220 Z" fill="#003D73" />
      <path d="M116 146 L132 172 L148 146 Z" fill="#FFFFFF" />
      <path d="M129 152 L135 152 L138 182 L132 190 L126 182 Z" fill="#005EAD" />
      <path d="M100 146 L132 196 L112 220 L96 220 Z" fill="#002B52" opacity="0.5" />
      <path d="M164 146 L132 196 L152 220 L168 220 Z" fill="#002B52" opacity="0.5" />
      <rect x="121" y="118" width="22" height="24" rx="8" fill={NECK} />
      <Face />
      <path d="M99 92 C98 66 113 56 133 56 C153 56 168 68 166 90 C158 78 141 74 122 78 C110 80 103 85 99 92 Z" fill={INK} />
      <Features />
    </svg>
  );
}

/** Developer at a laptop in front of a code window. */
export function DeveloperArt({ label }: { label: string }) {
  return (
    <svg viewBox="0 0 320 220" width="100%" role="img" aria-label={label} style={{ display: 'block' }}>
      <Dots x={264} y={164} />
      <circle cx="132" cy="120" r="88" fill="#D6E6F7" />
      <rect x="194" y="26" width="108" height="96" rx="8" fill={INK} />
      <circle cx="206" cy="38" r="3" fill="#F04438" />
      <circle cx="216" cy="38" r="3" fill="#F79009" />
      <circle cx="226" cy="38" r="3" fill="#17B26A" />
      <rect x="206" y="52" width="40" height="5" rx="2.5" fill="#7FB0E0" />
      <rect x="216" y="64" width="56" height="5" rx="2.5" fill="#FFFFFF" opacity="0.85" />
      <rect x="216" y="76" width="34" height="5" rx="2.5" fill="#FEC84B" />
      <rect x="226" y="88" width="48" height="5" rx="2.5" fill="#7FB0E0" />
      <rect x="206" y="100" width="28" height="5" rx="2.5" fill="#FFFFFF" opacity="0.85" />
      <path d="M62 220 C62 168 90 146 132 146 C174 146 202 168 202 220 Z" fill="#005EAD" />
      <path d="M114 148 C118 160 146 160 150 148" fill="none" stroke="#004C8C" strokeWidth="5" strokeLinecap="round" />
      <rect x="121" y="118" width="22" height="26" rx="8" fill={NECK} />
      <Face />
      <path d="M99 94 C95 66 112 54 132 55 C150 56 168 66 166 92 C162 82 155 76 147 78 C141 70 128 70 120 76 C110 76 103 84 99 94 Z" fill={INK} />
      <Features />
      <rect x="84" y="168" width="96" height="44" rx="5" fill="#344054" />
      <circle cx="132" cy="190" r="6" fill="#7FB0E0" />
      <path d="M66 220 L74 210 L190 210 L198 220 Z" fill="#D0D5DD" />
    </svg>
  );
}

/** Small round head icon for a person, e.g. next to "Developed by". Mike gets his illustration's hair. */
export function PersonAvatar({ name, size = 36 }: { name: string; size?: number }) {
  const isMike = /mike min myat oo/i.test(name);
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
      <circle cx="32" cy="32" r="32" fill="#D6E6F7" />
      <path d="M8 64 C8 50 18 43 32 43 C46 43 56 50 56 64 Z" fill={isMike ? '#005EAD' : '#7FB0E0'} />
      {isMike && <path d="M25 44 C27 49 37 49 39 44" fill="none" stroke="#004C8C" strokeWidth="2.5" strokeLinecap="round" />}
      <rect x="27" y="34" width="10" height="11" rx="4" fill={NECK} />
      <circle cx="18.5" cy="28" r="3.5" fill={EARS} />
      <circle cx="45.5" cy="28" r="3.5" fill={EARS} />
      <circle cx="32" cy="26" r="14" fill={SKIN} />
      {isMike ? (
        <path d="M18 26 C16 14 23 9 32 9.5 C40 10 47 14 46 25 C44 21 41 18 38 19 C35 15 29 15 26 18 C22 18 19 21 18 26 Z" fill={INK} />
      ) : (
        <path d="M18 25 C18 14 24 10 32 10 C40 10 46 14 46 24 C42 19 36 17 30 18 C25 19 21 21 18 25 Z" fill={INK} />
      )}
      <circle cx="27" cy="28" r="1.6" fill={INK} />
      <circle cx="37" cy="28" r="1.6" fill={INK} />
      <path d="M27.5 33 Q32 36.5 36.5 33" fill="none" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
